import React, { useState, useRef, useEffect, useId } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import Imagen from "@/component/c-image";
import Icon from "@gc-npm/components.icon-glyph";
import Label, { LABEL_SHAPES, LABEL_TYPES } from "@gc-npm/components.cms-block.cms-label";
import CustomLink from "@gc-npm/components.custom-link";
import { ImageSliderCSS } from "./style";
import IconGlyph from "@gc-npm/components.icon-glyph";
import { actions as modalActions } from "@gc-npm/reducers.modals-reducer";
import VideoModal from "@/modals/video";
import { TEXT_SIZE } from "@gc-npm/components.text";

const MediaSlider = ({ hash = "", mediaUrls, labelPromo, name, stamp }) => {
  const dispatch = useDispatch();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [startX, setStartX] = useState(0);
  const [isMoving, setIsMoving] = useState(false);
  const sliderRef = useRef(null);
  const instructionsId = useId();
  const { nameProduct } = useSelector(
    (state) => state.device
  );

  const handleTouchStart = (event) => {
    setStartX(event.touches[0].clientX);
  };

  const handleTouchMove = (event) => {
    const currentX = event.touches[0].clientX;
    const differenceX = startX - currentX;

    if (!isMoving && differenceX > 50) {
      // Move to left
      setIsMoving(true);
      setCurrentIndex((prevIndex) => (prevIndex + 1) % mediaUrls.length);
    } else if (!isMoving && differenceX < -50) {
      // Move to right
      setIsMoving(true);
      setCurrentIndex(
        (prevIndex) => (prevIndex - 1 + mediaUrls.length) % mediaUrls.length
      );
    }
  };

  const handleChangeSlide = (index) => {
    if (index > currentIndex) {
      setIsMoving(true);
      setCurrentIndex(index % mediaUrls.length);
    } else if (index < currentIndex) {
      setIsMoving(true);
      setCurrentIndex((index + mediaUrls.length) % mediaUrls.length);
    }
  };

  const handleSliderKeyDown = (event) => {
    if (event.target !== event.currentTarget) return;

    const nextIndex = {
      ArrowLeft: currentIndex - 1,
      ArrowRight: currentIndex + 1,
      Home: 0,
      End: mediaUrls.length - 1,
    }[event.key];
    if (nextIndex === undefined) return;

    event.preventDefault();
    handleChangeSlide(nextIndex);
  };

  const handleTransitionEnd = () => {
    setIsMoving(false);
  };

  const closeModal = async () => {
    dispatch(modalActions.addModal({ id: "", omitAnchorListener: true }));
  };

  const launchVideoModal = (videoSrc) => {
    dispatch(
      modalActions.addModal({
        id: "DeviceVideoModal",
        Element2Show: VideoModal,
        modalProps: {
          contentPosition: "center--center",
          inSceneEffect: "fadeIn",
          outSceneEffect: "fadeOut",
          opacityLayoutColor: "darkblue",
          closeByClickingOutside: true,
        },
        elementProps: {
          videoSrc,
          closeModal,
        },
      })
    );
  };

  useEffect(() => {
    if (mediaUrls) {
      setCurrentIndex(0);
    }
  }, [mediaUrls]);

  const sliderStyles = {
    transform: `translateX(-${currentIndex * 100}%)`,
    transition: isMoving ? "transform 0.3s ease-in-out" : "none",
  };

  if (
    !mediaUrls ||
    !Array.isArray(mediaUrls) ||
    (Array.isArray(mediaUrls) && mediaUrls.length < 1)
  ) {
    return null;
  }

  return (
    <section className="section-device-slider">
      <div className="column-images" role="group" aria-label={`Miniaturas de ${nameProduct || name}`}>
        {mediaUrls.map((mediaUrl, index) => (
          mediaUrl && (
            <div
              key={`column-image-${index.toString()}`}
              className={`column-images--container ${index === currentIndex ? "column-images--container--active" : ""
                }`}
            >
              <CustomLink
                className="custom-link-image-slider"
                taggingInfo={{
                  componentContent: nameProduct,
                  componentName: "image-slider",
                }}
                onClick={() => handleChangeSlide(index)}
                ariaLabel={`${nameProduct}, miniatura ${index + 1} de ${mediaUrls.length}`}
                ariaCurrent={index === currentIndex ? "true" : undefined}
              >
                {
                  index === 0 && (stamp && stamp.icon ? (
                    <div className="column-images--container--stamp" style={{ justifyContent: stamp.align }}>
                      <Icon
                        id={stamp.icon}
                        width={stamp.columnThumbWidth || 50}
                        height="auto"
                        position="right"
                      />
                    </div>
                  ) : (
                    stamp && stamp.img && (
                      <div className="column-images--container--stamp" style={{ justifyContent: stamp.align }}>
                        <Imagen
                          src={stamp.img}
                          title={name}
                          alt={name}
                          hash={hash}
                          height={100}
                          width={stamp.columnThumbWidth || 50}
                          cydata="mainImageCardIn"                          
                        />
                      </div>
                    )
                  ))
                }
                <div
                  className="column-images--container--image-box"
                  style={{ position: "relative" }}
                >
                  {(mediaUrl?.includes(".mp4") || mediaUrl?.includes(".webm")) && (
                    <div className="column-images--container--image-box--play-button">
                      <span className="background"></span>
                      <IconGlyph
                        id="play-m"
                        height={25}
                        color="white"
                      />
                    </div>
                  )}
                  <Imagen
                    hash={hash}
                    src={mediaUrl?.includes(".mp4") || mediaUrl?.includes(".webm") ? mediaUrls[0] : mediaUrl}
                    alt={`${nameProduct}`}
                    objectFit="contain"
                    sizes="100px"
                    fill
                  />
                </div>
              </CustomLink>
            </div>
          )
        ))}
      </div>
      <div
        className="device-slider-container"
        role="group"
        aria-roledescription="carrusel"
        aria-label={`Imágenes y vídeos de ${nameProduct || name}`}
        aria-describedby={mediaUrls.length > 1 ? instructionsId : undefined}
        tabIndex={mediaUrls.length > 1 ? 0 : undefined}
        onKeyDown={handleSliderKeyDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
      >
        <span id={instructionsId} className="device-slider-instructions">
          Usa las flechas izquierda y derecha para cambiar de imagen o vídeo, o Inicio y Fin para ir al primero o al último.
        </span>
        <span className="device-slider-instructions" aria-live="polite" aria-atomic="true">
          {`Elemento ${currentIndex + 1} de ${mediaUrls.length}`}
        </span>
        <div className="device-slider-container--label">
          {labelPromo && labelPromo.text && (
            <Label
              className="crazy-label label-images-slider"
              cydata="labelPromoEntrega"
              type={LABEL_TYPES.ACTIVE}
              shape={LABEL_SHAPES.TEARS}
              text={labelPromo.text}
              textSize={TEXT_SIZE.TEXT_PRESET_02}
            />
          )}
        </div>
        <div
          className={`device-slider ${isMoving ? "moving" : ""}`}
          style={sliderStyles}
          ref={sliderRef}
          onTransitionEnd={handleTransitionEnd}
        >
          {mediaUrls.map((mediaUrl, index) => (
            <div
              className={`device-slider--slide`}
              key={`device-image-slider-${index.toString()}`}
              style={{ position: "relative" }}
              aria-hidden={index !== currentIndex}
              inert={index !== currentIndex ? "" : undefined}
            >
              {
                index === 0 && (stamp && stamp.icon ? (
                  <div className="device-slider--slide--stamp" style={{ justifyContent: stamp.align }}>
                    <Icon
                      id={stamp.icon}
                      width={stamp.activeSlideWidth || 200}
                      height="auto"
                      position="right"
                    />
                  </div>
                ) : (
                  stamp && stamp.img && (
                    <div className="device-slider--slide--stamp" style={{ justifyContent: stamp.align }}>
                      <Imagen
                        src={stamp.img}
                        title={name}
                        alt={name}
                        hash={hash}
                        height={100}
                        width={stamp.activeSlideWidth || 200}
                        cydata="mainImageCardIn"
                      />
                    </div>
                  )
                ))
              }
              <div
                className="device-slider--slide--image-box"
                style={{ position: "relative" }}
              >
                {(mediaUrl?.includes(".mp4") || mediaUrl?.includes(".webm")) && (
                  <CustomLink
                    onClick={() => launchVideoModal(mediaUrl)}
                    ariaLabel={`Reproducir vídeo de ${nameProduct || name}`}
                    tabIndex={index === currentIndex ? "0" : "-1"}
                    className="device-slider--slide--image-box--play-button"
                  >
                    <span className="background"></span>
                    <span aria-hidden="true">
                      <IconGlyph
                        id="play-m"
                        height={50}
                        color="white"
                      />
                    </span>
                  </CustomLink>
                )}
                <Imagen
                  hash={hash}
                  src={mediaUrl?.includes(".mp4") || mediaUrl?.includes(".webm") ? mediaUrls[0] : mediaUrl}
                  alt={`${nameProduct}`}
                  title={`${nameProduct}`}
                  objectFit="contain"
                  sizes="(min-width: 768px) 520px, 100vw"
                  fill
                />
              </div>
            </div>
          ))}
        </div>
        <div className="device-slider-counter">
          {mediaUrls.map((image, index) => (
            <div key={`device-slider-counter--${index}`} className={index === currentIndex ? "device-slider-counter--bullet-active" : "device-slider-counter--bullet"} />
          ))}
        </div>
      </div>
      <style jsx>{ImageSliderCSS}</style>
    </section>
  );
};

MediaSlider.propTypes = {
  /**
   * Array de imágenes y video que se van a renderizar en el slider
   */
  mediaUrls: PropTypes.array.isRequired,
  /**
   * Label que se pinta si el producto tiene una promo
   */
  labelPromo: PropTypes.object,
};

MediaSlider.defaultProps = {
  labelPromo: null,
};

export default MediaSlider;
